import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import useAuth from '../../auth/useAuth';

import { parseDescription } from '../../utils';

import { useSoundStatus } from '../../context/SoundStatusContext';
import { useTour } from '../../context/TourContext';

import Layout from '../UI/introGameLayout/layout';

import BackgroundImage from './images/game-background-blurred.jpg';

import {
  getDecisions,
  makeDecision,
  updateDecision,
  updateGameSession,
  postDecisionsBulk,
} from './api';

import { gameQuestionsLimit } from './data';

import {
  validateDecision,
  calculateScore,
  getScoreAccuracy,
  saveSessionToStorage,
  loadSessionFromStorage,
  clearSessionFromStorage,
  formatDecisionsForBulkPost
} from './validation';

import Modal from '../UI/modal';
import BalanceSheet from '../UI/balancedEquation/balanceSheet';

import TransactionRecords from '../UI/balancedEquation/transactionRecords';

import './styles.css';

import GameProcessSound from './sounds/game-process.mp3';
import ClickSound from './sounds/click.mp3';
import UpdateBalanceSheetSound from './sounds/update-balance-sheet.mp3';
import AnswerCorrectSound from './sounds/answer-correct.mp3';
import AnswerWrongSound from './sounds/answer-wrong.mp3';
import ItemDroppedSound from './sounds/item-dropped.mp3';

import SignMinus from './images/icon-minus.svg';
import SignPlus from './images/icon-plus.svg';
import Intro from '../UI/game/intro';

// Map of scenario-level name → reflection tour name. Levels listed here
// share the equation-box / balance-sheet two-pane layout, so the
// TransactionRecords / BalanceSheet components also receive matching
// `tourId` props for highlighting.
const REFLECTION_TOURS_BY_LEVEL = {
  'increase-decrease': 'increase-decrease-reflection',
  'debit-credit': 'debit-credit-reflection',
  // Post-rework "Debit and Credit" levels share the reflection tour
  // with the legacy variant — both discuss the same debit/credit rules,
  // even though the new gameplay layers increase/decrease on top.
  'debit-credit2': 'debit-credit-reflection',
  'accounts-impacted': 'accounts-impacted-reflection',
  'income-statement-impact': 'income-statement-impact-reflection',
};

export default function BalancedEquation({ levelNumber, scenarioLevels, answerData, games, gamesSessions, game }) {
  const gameProcessEffect = useRef(new Audio(GameProcessSound));

  const clickEffect = useRef();
  const itemDroppedEffect = useRef();
  const updateBalanceSheetEffect = useRef();
  const answerCorrectEffect = useRef();
  const answerWrongEffect = useRef();

  useEffect(() => {
    const clickAudio = new Audio(ClickSound);  
    clickAudio.preload = 'auto';  
    clickEffect.current = clickAudio;

    const itemDroppedAudio = new Audio(ItemDroppedSound);  
    itemDroppedAudio.preload = 'auto';  
    itemDroppedEffect.current = itemDroppedAudio;
    itemDroppedEffect.current.volume = 0.18;

    const updateBalanceSheetAudio = new Audio(UpdateBalanceSheetSound);  
    updateBalanceSheetAudio.preload = 'auto';  
    updateBalanceSheetEffect.current = updateBalanceSheetAudio;
    updateBalanceSheetEffect.current.volume = 0.3;
    
    const answerCorrectAudio = new Audio(AnswerCorrectSound);  
    answerCorrectAudio.preload = 'auto';  
    answerCorrectEffect.current = answerCorrectAudio;
    
    const answerWrongAudio = new Audio(AnswerWrongSound);  
    answerWrongAudio.preload = 'auto';  
    answerWrongEffect.current = answerWrongAudio;
  
    return () => {
      clickAudio.pause();
      itemDroppedAudio.pause();
      updateBalanceSheetAudio.pause();
      answerCorrectAudio.pause();
      answerWrongAudio.pause();
    };
  }, []);

  const navigate = useNavigate();

  const {
    getUser,
    getCurrentBalancedEquationGameId
  } = useAuth();

  const user = getUser();
  const gameId = getCurrentBalancedEquationGameId();

  const { soundStatus } = useSoundStatus();
  const { startTour } = useTour();

  const [questions, setQuestions] = useState([]);
  const [activeQuestionId, setActiveQuestionId] = useState(0);
  const [questionAnsweredCorrectly, setQuestionAnsweredCorrectly] = useState(0);

  const [questionTries, setQuestionTries] = useState({});
  const [previouslyCorrectDecisions, setPreviouslyCorrectDecisions] = useState([]);
  const [allDecisions, setAllDecisions] = useState([]);
  const balanceSheetBeforeQuestionRef = useRef(null);

  const postInFlightRef = useRef(null);
  const sessionUpdateInFlightRef = useRef(null);

  const [topOptions, setTopOptions] = useState([]);
  const [changeTypeOptions, setChangeTypeOptions] = useState([]);
  const [coreTypeOptions, setCoreTypeOptions] = useState([]);
  const [transactionTypeOptions, setTransactionTypeOptions] = useState([]);

  const [isPlaying, setIsPlaying] = useState(false);

  const [isIntroVisible, setIsIntroVisible] = useState(true);
  const [scenarioLevel, setScenarioLevel] = useState('');

  const [selectedDesignTypes, setSelectedDesignTypes] = useState([]);

  
  const [isGameBelongsToTournament, setIsGameBelongsToTournament] = useState(null);

  const [leftIntroTitle, setLeftIntroTitle] = useState(null);
  const [leftIntroSubtitle, setLeftIntroSubtitle] = useState(null);
  const [leftIntroDescription, setLeftIntroDescription] = useState(null);

  const [rightIntroTitle, setRightIntroTitle] = useState(null);
  const [rightIntroSubtitle, setRightIntroSubtitle] = useState(null);
  const [rightIntroDescription, setRightIntroDescription] = useState(null);

  const [isIntroContentCentered, setIsIntroContentCentered] = useState(false);
  const [isIntroSubtitleContentReversed, setIsIntroSubtitleContentReversed] = useState(false);
  useEffect(() => {
    if(gameId && scenarioLevel) {
      setIsGameBelongsToTournament(game?.tournament_ids?.length > 0);
      setLeftIntroTitle(`Level ${levelNumber}`);
      const { subtitle, description } = parseDescription(scenarioLevel.description);
      setLeftIntroSubtitle(subtitle);
      setRightIntroDescription(description);
    }
  }, [game, gameId, levelNumber, scenarioLevel]);

  const handleIntroClose = () => {
    setIsIntroVisible(false);
  }

  useEffect(() => {
    const playClickSound = () => {
      if (soundStatus === 'enabled') {
        clickEffect.current.play().catch(e => console.error('Error playing click sound:', e));
      }
    };

    document.addEventListener('click', playClickSound);

    return () => {
      document.removeEventListener('click', playClickSound);
    };
  }, [soundStatus]);

  /* Prevent sound from playing on other pages */
  useEffect(() => {
    const currentGameProcessEffect = gameProcessEffect.current;
  
    return () => {
      if (currentGameProcessEffect) {
        currentGameProcessEffect.pause();
      }
    };
  }, [window.location]);

  /* play background music */
  useEffect(() => {  
    if (isPlaying && soundStatus === 'enabled') {
      gameProcessEffect.current.volume = 0.09;
      gameProcessEffect.current.loop = true;
      gameProcessEffect.current.play().catch(e => console.log('Error playing game process sound:', e));
    } else {
      gameProcessEffect.current.pause();
    }
  
    return () => {
      gameProcessEffect.current.pause();
    };
  }, [isPlaying, soundStatus]);

  const [totalScore, setTotalScore] = useState(0);

  const [isModalVisible, setIsModalVisible] = useState(false);

  const [assets, setAssets] = useState(0);
  const [liabilities, setLiabilities] = useState(0);
  const [equity, setEquity] = useState(0);

  const [assetsChildren, setAssetsChildren] = useState([]);
  const [liabilitiesChildren, setLiabilitiesChildren] = useState([]);
  const [equityChildren, setEquityChildren] = useState([]);

  const resetBalanceSheet = () => {
    setIsIntroVisible(true);

    setTotalScore(0);
    setQuestions([]);
    setActiveQuestionId(0);
    setQuestionAnsweredCorrectly(0);
    setTime(0);
    setTimeFromAppear(0);
    setSelectedDesignTypes([]);
    setFilledRecords([]);
    setOptionsToBeFilled(0);
    setAnswerStatus(null);
    setIncorrectDecisions([]);
    setIsItemsDisabled(false);
    setScenarioLevel(null);

    setAssets(0);
    setLiabilities(0);
    setEquity(0);

    assetsChildrenRef.current = [];
    liabilitiesChildrenRef.current = [];
    equityChildrenRef.current = [];
    setAssetsChildren([]);
    setLiabilitiesChildren([]);
    setEquityChildren([]);
  }

  const assetsChildrenRef = useRef(assetsChildren);
  const liabilitiesChildrenRef = useRef(liabilitiesChildren);
  const equityChildrenRef = useRef(equityChildren);

  const [time, setTime] = useState(0);
  const timeRef = useRef(time);
  useEffect(() => {
    timeRef.current = time;
  }, [time]);

  useEffect(() => {
    if (!isPlaying) return;
    const timerInterval = setInterval(() => {
      const newTime = timeRef.current + 1;
      setTime(newTime);
    }, 1000);

    return () => clearInterval(timerInterval);
  }, [isPlaying]);

  const [answerStatus, setAnswerStatus] = useState(null);
  const [isExplanationVisible, setIsExplanationVisible] = useState(false);
  const [explanationContent, setExplanationContent] = useState(null);

  const [timeFromAppear, setTimeFromAppear] = useState(0);

  const [isSubmitButtonShown, setIsSubmitButtonShown] = useState(true);

  const [isNextLevelAvailable, setIsNextLevelAvailable] = useState(true);
  const [isNextPageLoading, setIsNextPageLoading] = useState(false);

  const { data: decisionsResponse, isSuccess: isDecisionsSuccess } = useQuery({
    queryKey: [`decisionsResponse_${levelNumber}`, { userId: user?.id, gameId, level: levelNumber }],
    queryFn: () => getDecisions({ userId: user?.id, gameId, level: levelNumber }),
    enabled: !!user?.id && !!gameId,
    refetchOnWindowFocus: false
  });

  const [currentGameSessionId, setCurrentGameSessionId] = useState(0);

  const queryClient = useQueryClient();

  const updateGameSessionMutation = useMutation({
    mutationFn: (data) => updateGameSession(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['generalInfo'] });
    }
  });

  useEffect(() => {
    if(games && gamesSessions && gameId && user?.id && !currentGameSessionId) {
      const currentGameTypeSessions = gamesSessions.filter(session => games.find(game => game.id === session.game && game.game_type === 'Balanced Equation'));
      if(gamesSessions.length && currentGameTypeSessions.length) {
        const currentGameSessions = currentGameTypeSessions.filter(session => session.user === user.id && session.game === gameId);
      
        if (currentGameSessions.length) {
          const currentNotCompletedGameSession = currentGameSessions.find(session => !session.completed);
          if(currentNotCompletedGameSession) {
            setCurrentGameSessionId(currentNotCompletedGameSession.id);
          } else {
            const completedGameSession = currentGameSessions
              .filter(session => session.completed)
              .reduce((maxSession, session) => (session.id > (maxSession?.id || 0) ? session : maxSession), null);

            setCurrentGameSessionId(completedGameSession?.id);
          }
        }
      }
    }
  }, [games, gamesSessions, gameId, user?.id]);

  

  useEffect(() => {
    if(gameId && gamesSessions) {
      const currentGameSession = gamesSessions
        .filter(session => session.user === user?.id && session.is_ready === true && session.game === gameId)
        .reduce((maxSession, session) => (session.id > (maxSession?.id || 0) ? session : maxSession), null);

      if (currentGameSession) {
        const firstIncompleteLevel = ['level1', 'level2', 'level3', 'level4', 'level5'].findIndex(level => currentGameSession[level] !== 'completed') + 1;

        if(currentGameSession.completed && currentGameSession[`level${levelNumber}`] === null && !isModalVisible) {
          navigate('/balanced-equation/level/1/');
        } else {
          if(levelNumber > firstIncompleteLevel) {
            navigate(`/balanced-equation/level/${firstIncompleteLevel}/`);
          }
        }
      }
    }
  }, [gamesSessions, navigate, user?.id, gameId]);

  // Replay guard: if this level is already completed in the resolved
  // session (e.g. the player hit the back button after completing it),
  // skip past the gameplay component and forward them to the next level
  // — or to the summary if this was the last level. BE has no points
  // page, so we match the natural post-completion flow used by the
  // "Next level" / "View Summary" button. Fires once per mount via a
  // ref so it can't re-trigger after the player completes this level
  // mid-session.
  const replayGuardCheckedRef = useRef(false);
  useEffect(() => {
    if (replayGuardCheckedRef.current) return;
    if (!gamesSessions || !currentGameSessionId || !scenarioLevels?.length) return;
    const session = gamesSessions.find(s => s.id === currentGameSessionId);
    if (!session) return;
    replayGuardCheckedRef.current = true;
    if (session[`level${levelNumber}`] === 'completed') {
      const isLastLevel = scenarioLevels.length === levelNumber;
      navigate(
        isLastLevel ? '/balanced-equation/summary' : `/balanced-equation/level/${levelNumber + 1}/`,
        { replace: true }
      );
    }
  }, [gamesSessions, currentGameSessionId, levelNumber, scenarioLevels, navigate]);

  // Restore from localStorage on mount when gameSessionId is known
  useEffect(() => {
    if (!currentGameSessionId) return;
    const savedSession = loadSessionFromStorage(currentGameSessionId);
    if (savedSession) {
      if (savedSession.questionTries) setQuestionTries(savedSession.questionTries);
      if (savedSession.allDecisions) setAllDecisions(savedSession.allDecisions);
      if (savedSession.answeredQuestionIds?.length) {
        const correctDecisions = savedSession.allDecisions
          ?.filter(d => d.is_correct)
          .map(d => ({ questionId: d.question, type: d.type, optionAccountItemId: d.item_option })) || [];
        setPreviouslyCorrectDecisions(correctDecisions);
      }
      if (savedSession.balanceSheet && savedSession.levelNumber === levelNumber) {
        const bs = savedSession.balanceSheet;
        setAssets(bs.assets || 0);
        setLiabilities(bs.liabilities || 0);
        setEquity(bs.equity || 0);
        if (bs.assetsChildren?.length) {
          setAssetsChildren(bs.assetsChildren);
          assetsChildrenRef.current = bs.assetsChildren;
        }
        if (bs.liabilitiesChildren?.length) {
          setLiabilitiesChildren(bs.liabilitiesChildren);
          liabilitiesChildrenRef.current = bs.liabilitiesChildren;
        }
        if (bs.equityChildren?.length) {
          setEquityChildren(bs.equityChildren);
          equityChildrenRef.current = bs.equityChildren;
        }
      }
    }
  }, [currentGameSessionId, levelNumber]);

  useEffect(() => {
    if (scenarioLevels?.length && isDecisionsSuccess && currentGameSessionId) {
      const scenarioLevel = scenarioLevels.find(scenarioLevel => scenarioLevel.level === levelNumber);
      const decisionsMade = decisionsResponse || [];
      const savedSession = loadSessionFromStorage(currentGameSessionId);
      setScenarioLevel(scenarioLevel);

      setIsNextLevelAvailable(scenarioLevels.length > levelNumber);

      const {
        topOptions,
        changeTypeOptions,
        coreTypeOptions,
        transactionTypeOptions
      } = scenarioLevel.be_options.reduce((acc, item) => {
        let { name, description } = item.account_item;
        if (!description) {
          description = name;
        }

        const formattedItem = {
          id: item.id,
          order: item.order,
          accountItemId: item.account_item.id,
          name,
          description,
          type: ''
        };

        if (['Assets', 'Liabilities', 'Equity'].includes(item.account_item.name)) {
          acc.topOptions.push(formattedItem);
        } else if (['Increase', 'Decrease'].includes(item.account_item.name)) {
          formattedItem.name = name === 'Increase' ? <img src={SignPlus} alt="Plus" /> : <img src={SignMinus} alt="Minus" />;
          formattedItem.type = 'change';
          acc.changeTypeOptions.push(formattedItem);
        } else if (['Debit', 'Credit'].includes(item.account_item.name)) {
          formattedItem.type = 'transaction';
          acc.transactionTypeOptions.push(formattedItem);
        } else {
          formattedItem.type = 'core';
          acc.coreTypeOptions.push(formattedItem);
        }

        return acc;
      }, { topOptions: [], changeTypeOptions: [], coreTypeOptions: [], transactionTypeOptions: [] });

      topOptions.sort((a, b) => a.order - b.order);
      changeTypeOptions.sort((a, b) => a.order - b.order);
      coreTypeOptions.sort((a, b) => a.order - b.order);
      // Force Debit before Credit so the drag-and-drop options render
      // in standard accounting order regardless of the backend's
      // stored `order` field.
      const transactionRank = (name) => (name === 'Debit' ? 0 : name === 'Credit' ? 1 : 2);
      transactionTypeOptions.sort((a, b) => transactionRank(a.name) - transactionRank(b.name));

      setTopOptions(topOptions);
      setChangeTypeOptions(changeTypeOptions);
      setCoreTypeOptions(coreTypeOptions);
      setTransactionTypeOptions(transactionTypeOptions);

      setSelectedDesignTypes(scenarioLevel.design);

      const countOptionsToBeFilled = !!changeTypeOptions.length + !!coreTypeOptions.length + !!transactionTypeOptions.length;
      setOptionsToBeFilled(countOptionsToBeFilled + (changeTypeOptions.length ? 1 : 0)); // +1 since we need to have non draggable value inside drop area if there is change type option

      const allQuestions = [];
      const answeredQuestions = [];
      let allCorrectDecisionsScore = 0;

      const changeTypeIds = changeTypeOptions.map(option => option.id);
      const coreTypeIds = coreTypeOptions.map(option => option.id);
      const transactionTypeIds = transactionTypeOptions.map(option => option.id);
      
      const savedAnsweredIds = savedSession?.answeredQuestionIds || [];

      scenarioLevel.be_questions
        .sort((a, b) => a.order - b.order)
        .forEach(question => {
          const relatedCorrectDecisions = decisionsMade.filter(decision => decision.question === question.id && decision.is_correct === true && decision.game_session === currentGameSessionId);
          const hasCorrectlyAnsweredDecision = relatedCorrectDecisions.some(decision => decision.question_answered_correctly === true) || savedAnsweredIds.includes(question.id);

          const questionScore = relatedCorrectDecisions.reduce((sum, decision) => sum + decision.score_points, 0);
          allCorrectDecisionsScore += questionScore;

          const questionData = {
            id: question.id,
            transactionId: question.transaction_id,
            value: question.value,
            description: question.description,
            order: question.order
          };

          allQuestions.push(questionData);
          
          if (hasCorrectlyAnsweredDecision) {
            answeredQuestions.push(questionData);
            if(scenarioLevel.design.includes('1')) {
              const uniqueCorrectChangeDecisions = [];
              const uniqueChangeCombinations = new Set();
  
              const filteredCorrectChangeDecisions = relatedCorrectDecisions.filter(decision => 
                changeTypeIds.includes(decision.selected_option)
              );
  
              for (const decision of filteredCorrectChangeDecisions) {
                const combinationKey = `${decision.item_option}-${decision.selected_option}`;
                if (!uniqueChangeCombinations.has(combinationKey)) {
                  uniqueCorrectChangeDecisions.push(decision);
                  uniqueChangeCombinations.add(combinationKey);
                }
                if (uniqueCorrectChangeDecisions.length >= 2) break; // We only need the first two unique decisions
              }
  
              for (const decision of uniqueCorrectChangeDecisions) {
                const relatedItemOption = topOptions.find(topOption => topOption.accountItemId === decision.item_option);
                const relatedChangeOption = changeTypeOptions.find(changeTypeOption => changeTypeOption.id === decision.selected_option);
  
                const value = relatedChangeOption.description === 'Increase' ? decision.value : -decision.value;
                balanceSheetTotalChange(relatedItemOption.name, value);
              }
            } else if(scenarioLevel.design.includes('2')) {
              const uniqueCorrectTransactionDecisions = [];
              const uniqueTransactionCombinations = new Set();
  
              const filteredCorrectTransactionDecisions = relatedCorrectDecisions.filter(decision => 
                transactionTypeIds.includes(decision.selected_option)
              );
  
              for (const decision of filteredCorrectTransactionDecisions) {
                const combinationKey = `${decision.item_option}-${decision.selected_option}`;
                if (!uniqueTransactionCombinations.has(combinationKey)) {
                  uniqueCorrectTransactionDecisions.push(decision);
                  uniqueTransactionCombinations.add(combinationKey);
                }
                if (uniqueCorrectTransactionDecisions.length >= 2) break; // We only need the first two unique decisions
              }
  
              for (const decision of uniqueCorrectTransactionDecisions) {
                const relatedItemOption = topOptions.find(topOption => topOption.accountItemId === decision.item_option);
                const relatedTransactionOption = transactionTypeOptions.find(transactionTypeOption => transactionTypeOption.id === decision.selected_option);
  
                const value = relatedItemOption.name === 'Assets'
                  ? relatedTransactionOption.name === 'Debit' ? decision.value : -decision.value
                  : relatedTransactionOption.name === 'Credit' ? decision.value : -decision.value;
                
                balanceSheetTotalChange(relatedItemOption.name, value);
              }
            }

            const uniqueCorrectCoreDecisions = [];
            const uniqueCoreCombinations = new Set();

            const filteredCorrectCoreDecisions = relatedCorrectDecisions.filter(decision => 
              coreTypeIds.includes(decision.selected_option)
            );

            for (const decision of filteredCorrectCoreDecisions) {
              const combinationKey = `${decision.item_option}-${decision.selected_option}`;
              if (!uniqueCoreCombinations.has(combinationKey)) {
                uniqueCorrectCoreDecisions.push(decision);
                uniqueCoreCombinations.add(combinationKey);
              }
              if (uniqueCorrectCoreDecisions.length >= 2) break; // We only need the first two unique decisions
            }
            
            for (const decision of uniqueCorrectCoreDecisions) {
              const relatedItemOption = topOptions.find(topOption => topOption.accountItemId === decision.item_option);
              const relatedCoreOption = coreTypeOptions.find(coreTypeOption => coreTypeOption.id === decision.selected_option);
              
              const relatedChangeOption = scenarioLevel.design.includes('1')
                ? changeTypeOptions.find(changeTypeOption => changeTypeOption.accountItemId === decision.selected_change)
                : transactionTypeOptions.find(transactionTypeOption => transactionTypeOption.accountItemId === decision.selected_normal);

              const value = scenarioLevel.design.includes('1')
                ? relatedChangeOption?.description === 'Increase' ? decision.value : -decision.value
                : relatedItemOption.name === 'Assets'
                    ? relatedChangeOption?.name === 'Debit' ? decision.value : -decision.value
                    : relatedChangeOption?.name === 'Credit' ? decision.value : -decision.value;
              
              balanceSheetChildrenChange(relatedItemOption.name, { name: relatedCoreOption.name, value }, 'add');
            }
          }
        });

      // Restore score: combine backend decisions with localStorage score
      const localScore = savedSession?.allDecisions
        ?.filter(d => d.is_correct && d.score_points !== null)
        .reduce((sum, d) => sum + d.score_points, 0) || 0;
      setTotalScore(allCorrectDecisionsScore + localScore);

      const localWrongQuestionIds = savedSession?.allDecisions
        ?.filter(d => !d.is_correct)
        .map(d => d.question) || [];

      const wrongDecisions = decisionsMade.filter(decision => decision.is_correct === false && decision.game_session === currentGameSessionId);
      const questionIdsWithWrongDecisions = new Set([
        ...wrongDecisions.map(decision => decision.question),
        ...localWrongQuestionIds
      ]);

      const unansweredQuestionsNew = allQuestions.filter(question => 
        !answeredQuestions.some(answered => answered.id === question.id)
      );

      const questionsWithOrder1or2or3 = unansweredQuestionsNew.filter(question => 
        question.order === 1 || question.order === 2 || question.order === 3
      );

      const questionsWithWrongDecisions = unansweredQuestionsNew.filter(question => 
        questionIdsWithWrongDecisions.has(question.id) && question.order !== 1 && question.order !== 2 && question.order !== 3
      );

      const otherUnansweredQuestions = unansweredQuestionsNew.filter(question => 
        !questionIdsWithWrongDecisions.has(question.id) && question.order !== 1 && question.order !== 2 && question.order !== 3
      );

      const currentTotal = answeredQuestions.length + questionsWithOrder1or2or3.length + questionsWithWrongDecisions.length;
      const additionalSlotsAvailable = Math.max(0, gameQuestionsLimit - currentTotal);

      const shuffledOtherQuestions = otherUnansweredQuestions.sort(() => 0.5 - Math.random());
      const randomQuestions = shuffledOtherQuestions.slice(0, additionalSlotsAvailable);

      const mixedQuestions = [...questionsWithWrongDecisions, ...randomQuestions].sort(() => 0.5 - Math.random());

      const finalQuestionsToShow = [...questionsWithOrder1or2or3, ...mixedQuestions];

      if(finalQuestionsToShow.length) {
        setQuestions(finalQuestionsToShow);
        setActiveQuestionId(finalQuestionsToShow[0].id);
      } else {
        setIsModalVisible(true);
      }

      if(finalQuestionsToShow.length) {
        setQuestions(finalQuestionsToShow);
        setActiveQuestionId(finalQuestionsToShow[0].id);
      } else {
        setIsModalVisible(true);
      }
    }
  }, [scenarioLevels, decisionsResponse, isDecisionsSuccess, levelNumber, currentGameSessionId]);

  useEffect(() => {
    if(!isIntroVisible && !isModalVisible && questions?.length) {
      setIsPlaying(true);
    }
  }, [isIntroVisible, isModalVisible, questions]);

  // Capture balance sheet state when a new question becomes active (before any drops)
  useEffect(() => {
    if (activeQuestionId) {
      balanceSheetBeforeQuestionRef.current = {
        assets, liabilities, equity,
        assetsChildren: [...assetsChildrenRef.current],
        liabilitiesChildren: [...liabilitiesChildrenRef.current],
        equityChildren: [...equityChildrenRef.current]
      };
    }
  }, [activeQuestionId]);

  const shouldUpdateBalanceSheetSoundPlayRef = useRef(true);

  const balanceSheetTotalChange = (optionName, value) => {
    if (soundStatus === 'enabled' && shouldUpdateBalanceSheetSoundPlayRef.current !== false && isPlaying) {
      shouldUpdateBalanceSheetSoundPlayRef.current = false;
      setTimeout(() => {
        updateBalanceSheetEffect.current.play().catch(e => console.log('Error playing item hit spike sound:', e));
        shouldUpdateBalanceSheetSoundPlayRef.current = true;
      }, 300);
    }

    if (optionName === "Assets") {
      setAssets(prevAssets => prevAssets + value);
    } else if (optionName === "Liabilities") {
      setLiabilities(prevLiabilities => prevLiabilities + value);
    } else if (optionName === "Equity") {
      setEquity(prevEquity => prevEquity + value);
    }
  }
  
  const balanceSheetChildrenChange = (optionName, item, action, canRemove = false) => {
    if (optionName === "Assets") {
      if (action === 'add') {
        const existingItemIndex = assetsChildrenRef.current.findIndex(child => child.name === item.name);
  
        if (existingItemIndex !== -1) {
          const updatedChildren = [...assetsChildrenRef.current];
          updatedChildren[existingItemIndex].value += item.value;
          if (updatedChildren[existingItemIndex].value === 0 && canRemove) {
            updatedChildren.splice(existingItemIndex, 1); // Remove the item if value is 0
          }
          setAssetsChildren(updatedChildren);
          assetsChildrenRef.current = updatedChildren;  // Update the ref
        } else {
          const newChildren = [...assetsChildrenRef.current, item];
          setAssetsChildren(newChildren);
          assetsChildrenRef.current = newChildren;  // Update the ref
        }
      } else if (action === 'remove') {
        const newChildren = assetsChildrenRef.current.filter(child => child.name !== item.name);
        setAssetsChildren(newChildren);
        assetsChildrenRef.current = newChildren;  // Update the ref
      }
    } else if (optionName === "Liabilities") {
      if (action === 'add') {
        const existingItemIndex = liabilitiesChildrenRef.current.findIndex(child => child.name === item.name);
  
        if (existingItemIndex !== -1) {
          const updatedChildren = [...liabilitiesChildrenRef.current];
          updatedChildren[existingItemIndex].value += item.value;
          if (updatedChildren[existingItemIndex].value === 0 && canRemove) {
            updatedChildren.splice(existingItemIndex, 1); // Remove the item if value is 0
          }
          setLiabilitiesChildren(updatedChildren);
          liabilitiesChildrenRef.current = updatedChildren;  // Update the ref
        } else {
          const newChildren = [...liabilitiesChildrenRef.current, item];
          setLiabilitiesChildren(newChildren);
          liabilitiesChildrenRef.current = newChildren;  // Update the ref
        }
      } else if (action === 'remove') {
        const newChildren = liabilitiesChildrenRef.current.filter(child => child.name !== item.name);
        setLiabilitiesChildren(newChildren);
        liabilitiesChildrenRef.current = newChildren;  // Update the ref
      }
    } else if (optionName === "Equity") {
      if (action === 'add') {
        const existingItemIndex = equityChildrenRef.current.findIndex(child => child.name === item.name);
  
        if (existingItemIndex !== -1) {
          const updatedChildren = [...equityChildrenRef.current];
          updatedChildren[existingItemIndex].value += item.value;
          if (updatedChildren[existingItemIndex].value === 0 && canRemove) {
            updatedChildren.splice(existingItemIndex, 1); // Remove the item if value is 0
          }
          setEquityChildren(updatedChildren);
          equityChildrenRef.current = updatedChildren;  // Update the ref
        } else {
          const newChildren = [...equityChildrenRef.current, item];
          setEquityChildren(newChildren);
          equityChildrenRef.current = newChildren;  // Update the ref
        }
      } else if (action === 'remove') {
        const newChildren = equityChildrenRef.current.filter(child => child.name !== item.name);
        setEquityChildren(newChildren);
        equityChildrenRef.current = newChildren;  // Update the ref
      }
    }
  };

  const [filledRecords, setFilledRecords] = useState([]);

  const [optionsToBeFilled, setOptionsToBeFilled] = useState(0);
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);

  // Every BE level enforces the canonical 2-line entry — one record on
  // each side of the equation. Decision-type count affects how many
  // chips each record must carry, not the record count itself.
  const requiredRecordsForLevel = 2;

  useEffect(() => {
    const matchingRecords = filledRecords.filter(record => record.droppedItemsCount === optionsToBeFilled);
    const recordsWithDroppedItems = filledRecords.filter(record => record.droppedItemsCount > 0);
    const required = requiredRecordsForLevel;

    // Debits must balance credits by line count — 1D+1C for 2-record
    // levels, 2D+2C for 4-record levels. Only applies when the level
    // surfaces transaction (debit/credit) chips; a no-op when
    // transactionId is absent from every record (e.g. increase-decrease
    // level).
    const txCounts = {};
    matchingRecords.forEach(r => {
      if (r.transactionId) txCounts[r.transactionId] = (txCounts[r.transactionId] || 0) + 1;
    });
    const txValues = Object.values(txCounts);
    const isBalanced = txValues.length === 0 || (txValues.length === 2 && txValues[0] === txValues[1]);

    // Same-account duplicate rule: two records on the same account
    // can't both carry the same change decision or the same core
    // (account) decision.
    let hasAccountConflict = false;
    for (let i = 0; i < matchingRecords.length && !hasAccountConflict; i++) {
      for (let j = i + 1; j < matchingRecords.length && !hasAccountConflict; j++) {
        const a = matchingRecords[i];
        const b = matchingRecords[j];
        if (a.optionAccountItemId === b.optionAccountItemId && (
          (a.changeId && b.changeId && a.changeId === b.changeId)
          || (a.coreId && b.coreId && a.coreId === b.coreId)
        )) {
          hasAccountConflict = true;
        }
      }
    }

    if (
      matchingRecords.length !== required
        || recordsWithDroppedItems.length !== required
        || !isBalanced
        || hasAccountConflict
    ) {
      setIsButtonDisabled(true);
    } else {
      setIsButtonDisabled(false);
    }
  }, [filledRecords, optionsToBeFilled, requiredRecordsForLevel]);

  const onDropAreaChange = ({
    id,
    droppedItemsCount,
    box,
    optionAccountItemId,
    optionName,
    changeId,
    changeAccountItemId,
    changeDescription,
    coreId,
    coreAccountItemId,
    coreName,
    transactionId,
    transactionAccountItemId,
    transactionName
  }) => {
    if (soundStatus === 'enabled') {
      itemDroppedEffect.current.play().catch(e => console.log('Error playing item dropped sound:', e));
    }

    setFilledRecords(prevRecords => {
      const recordExists = prevRecords.some(record => record.id === id);
      let updatedRecords;
  
      if(recordExists) {
        updatedRecords = prevRecords.map(record => {
          if(record.id === id) {
            const updatedRecord = { ...record, droppedItemsCount };
            if(changeId) {
              updatedRecord.changeId = changeId;
              updatedRecord.changeAccountItemId = changeAccountItemId;
              updatedRecord.changeDescription = changeDescription;
            } else if(changeId === 0) {
              updatedRecord.changeId = null;
              updatedRecord.changeAccountItemId = null;
              updatedRecord.changeDescription = null;
            }
            if(coreId) {
              updatedRecord.coreId = coreId;
              updatedRecord.coreAccountItemId = coreAccountItemId;
              updatedRecord.coreName = coreName;
            } else if(coreId === 0) {
              updatedRecord.coreId = null;
              updatedRecord.coreAccountItemId = null;
              updatedRecord.coreName = null;
            }
            if(transactionId){
              updatedRecord.transactionId = transactionId;
              updatedRecord.transactionAccountItemId = transactionAccountItemId;
              updatedRecord.transactionName = transactionName;
            } else if(transactionId === 0) {
              updatedRecord.transactionId = null;
              updatedRecord.transactionAccountItemId = null;
              updatedRecord.transactionName = null;
            }
            return updatedRecord;
          }
          return record;
        });
      } else {
        const recordItem = {
          id,
          droppedItemsCount,
          box,
          optionAccountItemId,
          optionName,
          changeId,
          changeAccountItemId,
          changeDescription,
          coreId,
          coreAccountItemId,
          coreName,
          transactionId,
          transactionAccountItemId,
          transactionName
        };
        updatedRecords = [...prevRecords, recordItem];
      }
  
      return updatedRecords;
    });
  };

  const decisionMutation = useMutation({
    mutationFn: (data) => makeDecision(data)
  });
  
  const updateDecisionMutation = useMutation({
    mutationFn: (data) => updateDecision(data)
  });
  
  const [resetKey, setResetKey] = useState(0);
  const [incorrectDecisions, setIncorrectDecisions] = useState([]);

  const handleNextButtonClick = () => {
    setResetKey(prevKey => prevKey + 1);
    setFilledRecords([]);
    setIsSubmitButtonShown(true);
    setIsExplanationVisible(false);
    setIncorrectDecisions([]);

    if(questions.length > 1) {
      setTimeFromAppear(timeRef.current);
      setActiveQuestionId(prevActiveQuestionId => {
        const currentIndex = questions.findIndex(question => question.id === prevActiveQuestionId);
        
        const nextIndex = (currentIndex === questions.length - 1) ? 0 : currentIndex + 1;
        
        return questions[nextIndex].id;
      });

      if(questionAnsweredCorrectly) {
        setQuestions(prevQuestions => prevQuestions.filter(question => question.id !== questionAnsweredCorrectly));
        setQuestionAnsweredCorrectly(0);
      }
    } else {
      if(questionAnsweredCorrectly) {
        setIsPlaying(false);
        setIsModalVisible(true);

        sessionUpdateInFlightRef.current = updateGameSessionMutation.mutateAsync({
          id: currentGameSessionId,
          levelNumber,
          status: 'completed',
          isSessionCompleted: scenarioLevels.length === levelNumber,
        }).catch(error => {
          console.error('Failed to update game session:', error);
        });

        postInFlightRef.current = (async () => {
          try {
            const savedSession = loadSessionFromStorage(currentGameSessionId);
            const decisionsToPost = savedSession?.allDecisions || allDecisions;
            if (decisionsToPost.length) {
              const formattedDecisions = formatDecisionsForBulkPost(decisionsToPost, timeRef.current);
              await postDecisionsBulk(formattedDecisions);
              clearSessionFromStorage(currentGameSessionId);
            }
          } catch (error) {
            console.error('Failed to post decisions:', error);
          }
        })();
      }
    }

    setIsItemsDisabled(false);
  }

  const handleSubmitButtonClick = () => {
    setIsSubmitButtonLoading(true);
    shouldUpdateBalanceSheetSoundPlayRef.current = false;

    const latestFilledRecords = filledRecords.filter(filledRecord => filledRecord.droppedItemsCount);
    const activeQuestion = questions.find(question => question.id === activeQuestionId);

    const timeElapsed = timeRef.current - timeFromAppear;
    // game_time_elapsed = cumulative seconds since game-start (the on-
    // screen timer value at submission time). Stored per decision so the
    // bulk POST sends a monotonically increasing value across rows.
    const gameTimeElapsedSeconds = Math.max(1, timeRef.current);

    const questionId = activeQuestion.id;
    const transactionId = activeQuestion.transactionId;
    const currentTries = (questionTries[questionId] || 0) + 1;
    setQuestionTries(prev => ({ ...prev, [questionId]: currentTries }));

    const decisionResults = [];
    const timestamp = new Date().toISOString();
    const groupId = timestamp.replace(/\D/g, '');

    const hasAllAvailableTypes = selectedDesignTypes.length === 3 &&
      selectedDesignTypes.includes('1') &&
      selectedDesignTypes.includes('2') &&
      selectedDesignTypes.includes('3');

    for (const record of latestFilledRecords) {
      if (record.changeId) {
        const isCorrect = validateDecision(answerData, transactionId, record, 'change');
        const scoreAccuracy = getScoreAccuracy({ isCorrect, questionId, previouslyCorrectDecisions, type: 'change', optionAccountItemId: record.optionAccountItemId });
        const scorePoints = scoreAccuracy === null ? null : (isCorrect ? calculateScore({ timeElapsedSeconds: timeElapsed, totalTries: currentTries }) : 0);

        decisionResults.push({
          id: record.changeId,
          isCorrect,
          type: 'change',
          description: record.changeDescription,
          optionAccountItemId: record.optionAccountItemId,
          optionName: record.optionName,
          isSecondDropArea: record.box === 'Bottom',
          questionId,
          score: scorePoints,
          score_accuracy: scoreAccuracy,
          name: record.changeDescription,
          value: activeQuestion.value
        });
      }

      if (record.transactionId) {
        const isCorrect = validateDecision(answerData, transactionId, record, 'transaction');
        const scoreAccuracy = getScoreAccuracy({ isCorrect, questionId, previouslyCorrectDecisions, type: 'transaction', optionAccountItemId: record.optionAccountItemId });
        const scorePoints = scoreAccuracy === null ? null : (isCorrect ? calculateScore({ timeElapsedSeconds: timeElapsed, totalTries: currentTries }) : 0);

        decisionResults.push({
          id: record.transactionId,
          isCorrect,
          type: 'transaction',
          optionAccountItemId: record.optionAccountItemId,
          optionName: record.optionName,
          isSecondDropArea: record.box === 'Bottom',
          questionId,
          score: scorePoints,
          score_accuracy: scoreAccuracy,
          name: record.transactionName,
          value: activeQuestion.value
        });
      }

      if (record.coreId) {
        const isCorrect = validateDecision(answerData, transactionId, record, 'core');
        const scoreAccuracy = getScoreAccuracy({ isCorrect, questionId, previouslyCorrectDecisions, type: 'core', optionAccountItemId: record.optionAccountItemId });
        const scorePoints = scoreAccuracy === null ? null : (isCorrect ? calculateScore({ timeElapsedSeconds: timeElapsed, totalTries: currentTries }) : 0);

        decisionResults.push({
          id: record.coreId,
          isCorrect,
          type: 'core',
          optionAccountItemId: record.optionAccountItemId,
          optionName: record.optionName,
          isSecondDropArea: record.box === 'Bottom',
          questionId,
          score: scorePoints,
          score_accuracy: scoreAccuracy,
          name: record.coreName,
          value: activeQuestion.value
        });
      }
    }

    // Store all decisions for batch POST later
    const newDecisions = decisionResults.map(dr => ({
      user: user?.id,
      game: gameId,
      game_session: currentGameSessionId,
      question: questionId,
      box: latestFilledRecords.find(r =>
        (dr.type === 'change' && r.changeId === dr.id) ||
        (dr.type === 'transaction' && r.transactionId === dr.id) ||
        (dr.type === 'core' && r.coreId === dr.id)
      )?.box,
      item_option: dr.optionAccountItemId,
      selected_option: dr.id,
      selected_change: dr.type === 'core' ? latestFilledRecords.find(r => r.coreId === dr.id)?.changeAccountItemId : null,
      selected_normal: dr.type === 'core' && !hasAllAvailableTypes ? latestFilledRecords.find(r => r.coreId === dr.id)?.transactionAccountItemId : null,
      time_elapsed: timeElapsed || 1,
      game_time_elapsed: gameTimeElapsedSeconds,
      timestamp,
      group_id: groupId,
      is_correct: dr.isCorrect,
      score_points: dr.score,
      score_accuracy: dr.score_accuracy,
      total_tries: currentTries,
      type: dr.type
    }));

    setAllDecisions(prev => [...prev, ...newDecisions]);

    // Track previously correct decisions for score_accuracy
    const newCorrect = decisionResults
      .filter(dr => dr.isCorrect)
      .map(dr => ({ questionId, type: dr.type, optionAccountItemId: dr.optionAccountItemId }));
    if (newCorrect.length) {
      setPreviouslyCorrectDecisions(prev => [...prev, ...newCorrect]);
    }

    /* balance sheet update sound can play after the correct or wrong decision sound is played */
    setTimeout(() => {
      shouldUpdateBalanceSheetSoundPlayRef.current = true;
    }, 500);

    const correctResults = decisionResults.filter(decisionResult => decisionResult.isCorrect);

    correctResults.filter(cr => cr.score !== null && cr.score > 0).forEach(cr => {
      setTotalScore(prevTotalScore => prevTotalScore + cr.score);
    });

    const allResultsCorrect = correctResults.length === decisionResults.length;

    // Find explanation from answer data
    const answerEntry = answerData?.find(a => a.transaction_id === transactionId);
    let explanation = answerEntry?.explanation || null;

    if (allResultsCorrect) {
      answerCorrectEffect.current.play().catch(e => console.log('Error playing answer correct sound:', e));

      setQuestionAnsweredCorrectly(questionId);
      setAnswerStatus('correct');

        let firstUpdatedOptionName = '';
        switch(latestFilledRecords[0].optionName) {
          case 'Assets':
            firstUpdatedOptionName = 'an "Asset"';
            break;
          case 'Liabilities':
            firstUpdatedOptionName = 'a "Liability"';
            break;
          case 'Equity':
            firstUpdatedOptionName = 'an "Equity"';
            break;
          default:
            firstUpdatedOptionName = 'an "Asset"';
            break;
        }
        
        let secondUpdatedOptionName = '';
        switch(latestFilledRecords[1].optionName) {
          case 'Assets':
            secondUpdatedOptionName = 'an "Asset"';
            break;
          case 'Liabilities':
            secondUpdatedOptionName = 'a "Liability"';
            break;
          case 'Equity':
            secondUpdatedOptionName = 'an "Equity"';
            break;
          default:
            secondUpdatedOptionName = 'an "Asset"';
            break;
        }

        if(!explanation) {
          const changeDescriptionFirst = latestFilledRecords[0].changeDescription ? `"${latestFilledRecords[0].changeDescription}"` : `be "${latestFilledRecords[0].transactionName.toLowerCase()}ed"`;
          const changeDescriptionSecond = latestFilledRecords[1].changeDescription ? `"${latestFilledRecords[1].changeDescription}"` : `be "${latestFilledRecords[1].transactionName.toLowerCase()}ed"`;
          switch(decisionResults.length) {
            case 2:
              explanation = `${firstUpdatedOptionName[0].toUpperCase() + firstUpdatedOptionName.slice(1)} should ${changeDescriptionFirst} and ${secondUpdatedOptionName} should ${changeDescriptionSecond}. Click "Next" to continue.`;
              break;
            case 4: {
              // 4 decision results come from either change+core levels
              // (accounts-impacted / income-statement-impact) or the
              // change+transaction level (debit-credit2). Detect which
              // shape the records actually have so we don't stringify a
              // null coreName. See tutorial.js for the same fix.
              const isChangeTransactionCase = latestFilledRecords.every(
                (r) => r.transactionName && !r.coreName
              );
              if (isChangeTransactionCase) {
                explanation = `${firstUpdatedOptionName[0].toUpperCase() + firstUpdatedOptionName.slice(1)} should "${latestFilledRecords[0].changeDescription}" and be "${latestFilledRecords[0].transactionName.toLowerCase()}ed". ${secondUpdatedOptionName} should "${latestFilledRecords[1].changeDescription}" and be "${latestFilledRecords[1].transactionName.toLowerCase()}ed". Click "Next" to continue.`;
              } else {
                explanation = `${firstUpdatedOptionName[0].toUpperCase() + firstUpdatedOptionName.slice(1)} account, "${latestFilledRecords[0].coreName}", should ${changeDescriptionFirst} and ${secondUpdatedOptionName} account, "${latestFilledRecords[1].coreName}", should ${changeDescriptionSecond}. Click "Next" to continue.`;
              }
              break;
            }
            case 6:
              explanation = `"${latestFilledRecords[0].coreName}" should be "${latestFilledRecords[0].transactionName}ed" since it’s ${firstUpdatedOptionName} that "${latestFilledRecords[0].changeDescription}d". "${latestFilledRecords[1].coreName}" should be "${latestFilledRecords[1].transactionName}ed" since it’s ${secondUpdatedOptionName} that "${latestFilledRecords[1].changeDescription}d". Click "Next" to continue.`;
              break;
            default:
              explanation = `${firstUpdatedOptionName[0].toUpperCase() + firstUpdatedOptionName.slice(1)} should "${latestFilledRecords[0].changeDescription}" and ${secondUpdatedOptionName} should "${latestFilledRecords[1].changeDescription}". Click "Next" to continue.`;
              break;
          }
        }

        // Append a chip-decision sentence when the level surfaces
        // both change (increase/decrease) AND transaction (debit/credit)
        // chips on every record — i.e. debit-credit2. Backend
        // explanations are written in prose regardless of level, so
        // the student loses reinforcement of the debit/credit
        // decision they just made.
        //
        // Template per side: "<Element> <change> on the <side> side."
        //   e.g. "Assets increase on the debit side. Liabilities
        //   increase on the credit side."
        //
        // Guarded on the backend text NOT already mentioning debit/
        // credit so per-level backend explanations (if ever added)
        // won't produce double wording.
        const hasChangeAndTransactionOnEveryRecord = latestFilledRecords.every(
          (r) => r.changeDescription && r.transactionName
        );
        if (
          hasChangeAndTransactionOnEveryRecord
          && explanation
          && !/debit|credit/i.test(explanation)
        ) {
          const firstElement = latestFilledRecords[0].optionName;
          const secondElement = latestFilledRecords[1].optionName;
          // Conjugate the change verb to match the element: Assets /
          // Liabilities are plural (bare verb), Equity is singular
          // (add "s").
          const conjugate = (element, change) => (
            element === 'Equity' ? `${change.toLowerCase()}s` : change.toLowerCase()
          );
          const firstChange = conjugate(firstElement, latestFilledRecords[0].changeDescription);
          const secondChange = conjugate(secondElement, latestFilledRecords[1].changeDescription);
          const firstSide = latestFilledRecords[0].transactionName.toLowerCase();
          const secondSide = latestFilledRecords[1].transactionName.toLowerCase();
          explanation += ` ${firstElement} ${firstChange} on the ${firstSide} side. ${secondElement} ${secondChange} on the ${secondSide} side.`;
        }

        setExplanationContent(explanation);
        setIsExplanationVisible(true);
      } else {
        answerWrongEffect.current.play().catch(e => console.log('Error playing answer wrong sound:', e));

        const currentIncorrectDecisions = decisionResults.filter(decisionResult => !decisionResult.isCorrect);
        setIncorrectDecisions(currentIncorrectDecisions);

        let coreAdjustmentValue;
        decisionResults.forEach(decision => {
          if (decision.type === 'change') {
            const lowerCaseOptionName = decision.optionName.toLowerCase();
            const adjustmentValue = decision.description === 'increase' ? -activeQuestion.value : activeQuestion.value;
            coreAdjustmentValue = adjustmentValue;
      
            switch (lowerCaseOptionName) {
              case 'assets':
                setAssets(prevAssets => prevAssets + adjustmentValue);
                break;
              case 'liabilities':
                setLiabilities(prevLiabilities => prevLiabilities + adjustmentValue);
                break;
              case 'equity':
                setEquity(prevEquity => prevEquity + adjustmentValue);
                break;
              default:
                console.error(`Unknown option name: ${lowerCaseOptionName}`);
            }
          } else if(decision.type === 'transaction' && !selectedDesignTypes.includes('1') && selectedDesignTypes.includes('2')) {
            const lowerCaseOptionName = decision.optionName.toLowerCase();

            const  oppositeChange = decision.name === 'Debit' ? 'Credit' : 'Debit';
            const adjustmentValue = lowerCaseOptionName === 'assets'
              ? oppositeChange === 'Debit' ? activeQuestion.value : -activeQuestion.value
              : oppositeChange === 'Credit' ? activeQuestion.value : -activeQuestion.value;

            coreAdjustmentValue = adjustmentValue;

            switch (lowerCaseOptionName) {
              case 'assets':
                setAssets(prevAssets => prevAssets + adjustmentValue);
                break;
              case 'liabilities':
                setLiabilities(prevLiabilities => prevLiabilities + adjustmentValue);
                break;
              case 'equity':
                setEquity(prevEquity => prevEquity + adjustmentValue);
                break;
              default:
                console.error(`Unknown option name: ${lowerCaseOptionName}`);
            }
          }
          else if (decision.type === 'core') {
            balanceSheetChildrenChange(decision.optionName, {name: decision.name, value: coreAdjustmentValue}, 'add', true);
          }
        });

        setAnswerStatus('incorrect');
        if(!explanation) {
          explanation = 'Some or all of your decisions are not correct. Don’t worry, you’ll have another chance to answer this question. Click "Next" to continue.';
        }
        setExplanationContent(explanation);

        setIsExplanationVisible(true);
      }

    setIsSubmitButtonShown(false);
    setIsSubmitButtonLoading(false);

    // Save to localStorage for refresh resilience
    saveSessionToStorage(currentGameSessionId, {
      gameId,
      scenarioLevelId: scenarioLevel?.id,
      levelNumber,
      questionTries: { ...questionTries, [questionId]: currentTries },
      allDecisions: [...allDecisions, ...newDecisions],
      answeredQuestionIds: allResultsCorrect
        ? [...new Set([...(loadSessionFromStorage(currentGameSessionId)?.answeredQuestionIds || []), questionId])]
        : (loadSessionFromStorage(currentGameSessionId)?.answeredQuestionIds || []),
      totalScore: correctResults.filter(cr => cr.score !== null && cr.score > 0).reduce((sum, cr) => sum + cr.score, 0),
      balanceSheet: allResultsCorrect
        ? {
            assets, liabilities, equity,
            assetsChildren: assetsChildrenRef.current,
            liabilitiesChildren: liabilitiesChildrenRef.current,
            equityChildren: equityChildrenRef.current
          }
        : balanceSheetBeforeQuestionRef.current,
      time: timeRef.current
    });

    setIsItemsDisabled(true);
  };

  const [isSubmitButtonLoading, setIsSubmitButtonLoading] = useState(false);

  const activeQuestion = questions.find(question => question.id === activeQuestionId);

  // Wait for the completion POST (if still in flight), then retry once if
  // localStorage still holds decisions — i.e. the completion POST didn't succeed.
  const ensureDecisionsPosted = async () => {
    if (postInFlightRef.current) {
      await postInFlightRef.current;
    }
    const savedSession = loadSessionFromStorage(currentGameSessionId);
    if (savedSession?.allDecisions?.length) {
      try {
        const formattedDecisions = formatDecisionsForBulkPost(savedSession.allDecisions, timeRef.current);
        await postDecisionsBulk(formattedDecisions);
        clearSessionFromStorage(currentGameSessionId);
      } catch (error) {
        console.error('Retry post decisions failed:', error);
      }
    }
  };

  // Tournament completion: retry any pending decisions post and close the tab
  // (these tabs are opened via window.open, so window.close is allowed).
  const handleCloseTabClick = async () => {
    await ensureDecisionsPosted();
    window.close();
  };

  const handleNextPageButtonClick = async () => {
    // Per-level reflection tour. Skip for the in-tournament intermediate
    // path (where the student is mid-competition) so the flow stays snappy.
    const isViewSummaryButton = !(isGameBelongsToTournament && scenarioLevels?.length !== levelNumber);
    const reflectionTour = REFLECTION_TOURS_BY_LEVEL[scenarioLevel?.name];
    if (reflectionTour && isViewSummaryButton) {
      setIsModalVisible(false);
      startTour(reflectionTour, { onComplete: () => { handleNextPageButtonClickInner(); } });
      return;
    }
    handleNextPageButtonClickInner();
  };

  const handleNextPageButtonClickInner = async () => {
    setIsNextPageLoading(true);
    let redirectUrl = `/the-principles/level/${levelNumber + 1}`;

    // Wait for the level-complete session update to land on the server
    // before navigating — Transition re-queries sessions on mount and will
    // bounce the user back to this level if the completion hasn't been
    // recorded yet.
    if (sessionUpdateInFlightRef.current) {
      await sessionUpdateInFlightRef.current;
    }

    if(scenarioLevels?.length === levelNumber) {
      redirectUrl = '/balanced-equation/summary';

      await ensureDecisionsPosted();
    } else {
      redirectUrl = `/balanced-equation/level/${levelNumber + 1}/`;
      const hasCompletedLevelTypeBefore = (levelName, sessionsForGameType) => {
        return sessionsForGameType.some(session => {
          const levelNames = session.level_names || {};
          return Object.entries(levelNames).some(([levelKey, name]) => {
            return name === levelName && session[levelKey] === 'completed';
          });
        });
      };

      const sessionsForGameType = gamesSessions?.filter(
        session =>
          session.user === user?.id &&
          games.find(game => game.id === session.game && game.game_type === 'Balanced Equation')
      );
      const nextScenarioLevel = scenarioLevels?.find(scenarioLevel => scenarioLevel.level === levelNumber + 1);

      const hasNeverCompletedThisType = !hasCompletedLevelTypeBefore(
        nextScenarioLevel?.name,
        sessionsForGameType
      );

      if (hasNeverCompletedThisType) {
        redirectUrl = '/acc/transition';
      }

      await ensureDecisionsPosted();
    }
    navigate(redirectUrl);

    setIsModalVisible(false);
    resetBalanceSheet();
  }

  const [isItemsDisabled, setIsItemsDisabled] = useState(false);

  const [isSubmitHintShown, setIsSubmitHintShown] = useState(false);
  const [submitButtonHintContent, setSubmitButtonHintContent] = useState(<span></span>);

  // Number of records the level requires for Submit to enable AND the
  // Every BE level enforces the canonical 2-line entry — one record on
  // each side of the equation. Decision-type count affects how many
  // chips each record must carry, not the record count itself.
  const getRequiredRecordsForLevel = () => 2;

  const handleSubmitButtonMouseEnter = () => {
    if(isSubmitButtonShown && isButtonDisabled) {
      let submitMessage = '';

      const recordsWithDroppedItems = filledRecords.filter(record => record.droppedItemsCount > 0);
      const required = getRequiredRecordsForLevel();

      if(recordsWithDroppedItems.length < required) {
        submitMessage = `To submit, there must be decisions in ${required} records.`;
      } else if(recordsWithDroppedItems.length === required) {
        const allHaveChangeId = recordsWithDroppedItems.every(record => record.changeId);
        const allHaveCoreId = recordsWithDroppedItems.every(record => record.coreId);
        const allHaveTransactionId = recordsWithDroppedItems.every(record => record.transactionId);
        if(!allHaveChangeId && selectedDesignTypes.includes('1')) {
          submitMessage = `Increase or decrease needs to be in ${required} records.`;
        } else if (!allHaveTransactionId  && selectedDesignTypes.includes('2')) {
          submitMessage = `A debit or a credit needs to be in ${required} records.`;
        } else if (!allHaveCoreId && selectedDesignTypes.includes('3')) {
          submitMessage = `An account needs to be in ${required} records.`;
        } else {
          // Pairwise duplicate check — same rule as before, extended
          // over every pair when required > 2.
          outer:
          for (let i = 0; i < recordsWithDroppedItems.length; i++) {
            for (let j = i + 1; j < recordsWithDroppedItems.length; j++) {
              const a = recordsWithDroppedItems[i];
              const b = recordsWithDroppedItems[j];
              if (a.optionAccountItemId === b.optionAccountItemId) {
                if (a.changeId && b.changeId && a.changeId === b.changeId) {
                  submitMessage = 'An increase or decrease decision is missing.';
                  break outer;
                }
                if (a.transactionId && b.transactionId && a.transactionId === b.transactionId) {
                  submitMessage = 'A debit or credit decision is missing.';
                  break outer;
                }
                if (a.coreId && b.coreId && a.coreId === b.coreId) {
                  submitMessage = 'An account decision is missing.';
                  break outer;
                }
              } else if (a.transactionId && b.transactionId && a.transactionId === b.transactionId) {
                submitMessage = 'A debit or credit decision is missing.';
                break outer;
              }
            }
          }
        }
      } else if (recordsWithDroppedItems.length > required) {
        submitMessage = `There are too many records; decisions should be in no more than ${required} records.`;
      }

      setSubmitButtonHintContent(submitMessage);
      setIsSubmitHintShown(true);
    }
  }

  const handleSubmitButtonMouseLeave = () => {
    setIsSubmitHintShown(false);
  }

  return(
    <Layout
      backgroundColor="rgba(0, 27, 59, 0.04)"
      isHeaderVisible={true}
      isFooterVisible={true}
      totalScore={totalScore}
      scoreLabel="Points"
      isExplanationVisible={isExplanationVisible}
      explanationName="Bailey"
      isAvatarVisible={false}
      answerStatus={answerStatus}
      explanationContent={explanationContent}
    >
      {
        isIntroVisible && (
          <Intro
            leftTitle={leftIntroTitle}
            leftSubtitle={leftIntroSubtitle}
            leftDescription={leftIntroDescription}
            rightTitle={rightIntroTitle}
            rightSubtitle={rightIntroSubtitle}
            rightDescription={rightIntroDescription}
            onShowingCompleted={handleIntroClose}
            isContentCentered={isIntroContentCentered}
            isSubtitleContentReversed={isIntroSubtitleContentReversed}
          />
      )}
      <TransactionRecords
        transactionText={activeQuestion?.description}
        value={activeQuestion?.value}
        topOptions={topOptions}
        changeTypeOptions={changeTypeOptions}
        coreTypeOptions={coreTypeOptions}
        transactionTypeOptions={transactionTypeOptions}
        onChangeItemDrop={balanceSheetTotalChange}
        onCoreItemDrop={balanceSheetChildrenChange}
        onDropAreaChange={onDropAreaChange}
        isSubmitButtonShown={isSubmitButtonShown}
        isSubmitHintShown={isSubmitHintShown}
        submitHintContent={submitButtonHintContent}
        onSubmitButtonClick={handleSubmitButtonClick}
        onSubmitButtonMouseEnter={handleSubmitButtonMouseEnter}
        onSubmitButtonMouseLeave={handleSubmitButtonMouseLeave}
        onNextButtonClick={handleNextButtonClick}
        isSubmitButtonLoading={isSubmitButtonLoading}
        isButtonDisabled={isButtonDisabled}
        resetKey={resetKey}
        incorrectDecisions={incorrectDecisions}
        isItemsDisabled={isItemsDisabled}
        level={levelNumber}
        selectedDesignTypes={selectedDesignTypes}
        equation={scenarioLevel?.equation}
        optionsToBeFilled={optionsToBeFilled}
        tourId={REFLECTION_TOURS_BY_LEVEL[scenarioLevel?.name] ? 'be-equation-box' : undefined}
      />
      <BalanceSheet
        assets={assets}
        liabilities={liabilities}
        equity={equity}
        assetsChildren={assetsChildren}
        liabilitiesChildren={liabilitiesChildren}
        equityChildren={equityChildren}
        tourId={REFLECTION_TOURS_BY_LEVEL[scenarioLevel?.name] ? 'be-balance-sheet' : undefined}
      />
      {isModalVisible && (
        <Modal
          content={
            <>
              <div className="modal-center vertical">
                <div><span>Well done!</span></div>
                {
                  isGameBelongsToTournament && scenarioLevels?.length === levelNumber
                    ? <button onClick={handleCloseTabClick} className="button-blue button-view-summary">Close</button>
                    : <button onClick={handleNextPageButtonClick} className={`button-blue button-view-summary ${isNextPageLoading ? 'loading' : ''}`} disabled={isNextPageLoading}>{isNextPageLoading ? '' : (isNextLevelAvailable ? 'Next level' : 'View Summary')}</button>
                }
              </div>
            </>
          }
          isModalSmall={true}
        />
      )}
    </Layout>
  );
}
